import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, Bot, LoaderCircle, Send, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, streamSSE } from "@/lib/api";
import { assets } from "@/lib/assets";
import { useAuth } from "@/contexts/AuthContext";
import "@/components/AIDoctorAssistant.css";
import "@/components/AIDoctorRichText.css";

function getSessionId(userId) {
  const key = `srd_doctor_session_${userId}`;
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const session = `doctor-${crypto.randomUUID()}`;
  localStorage.setItem(key, session);
  return session;
}

function getDismissed(userId) {
  try { return new Set(JSON.parse(localStorage.getItem(`srd_doctor_dismissed_${userId}`) || "[]")); }
  catch { return new Set(); }
}

export function AIDoctorAssistant() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [bubbleVisible, setBubbleVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => getDismissed(user.user_id));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useMemo(() => getSessionId(user.user_id), [user.user_id]);
  const previousSignature = useRef(null);
  const messagesEnd = useRef(null);
  const historyLoaded = useRef(false);
  const historyRequest = useRef(null);
  const siteId = localStorage.getItem(`srd_site_id_${user.user_id}`) || user.primary_site_id || "";
  const { data = { suggestions: [], signature: "" } } = useQuery({
    queryKey: ["doctor-briefing", user.user_id, siteId],
    queryFn: () => api.get("/assistant/briefing", { params: siteId ? { site_id: siteId } : {} }).then((response) => response.data),
    refetchInterval: 10000,
  });
  const currentSuggestion = data.suggestions.find((item) => !dismissed.has(item.id));
  const currentSuggestionId = currentSuggestion?.id;

  useEffect(() => {
    if (!data.signature || !currentSuggestionId) return undefined;
    const changed = previousSignature.current && previousSignature.current !== data.signature;
    const timer = window.setTimeout(() => setBubbleVisible(true), changed ? 500 : 1600);
    previousSignature.current = data.signature;
    return () => window.clearTimeout(timer);
  }, [data.signature, currentSuggestionId]);

  const loadHistory = useCallback(async () => {
    if (historyLoaded.current) return;
    if (!historyRequest.current) {
      historyRequest.current = api.get("/assistant/history", { params: { session_id: sessionId } });
    }
    try {
      const response = await historyRequest.current;
      setMessages((current) => current.length ? current : response.data);
    } catch {
      setMessages((current) => current);
    } finally {
      historyLoaded.current = true;
    }
  }, [sessionId]);

  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);

  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const dismiss = (suggestion) => {
    const next = new Set(dismissed); next.add(suggestion.id); setDismissed(next);
    localStorage.setItem(`srd_doctor_dismissed_${user.user_id}`, JSON.stringify([...next]));
    setBubbleVisible(false);
  };
  const takeAction = (suggestion) => { setBubbleVisible(false); setOpen(false); navigate(suggestion.path); };
  const sendMessage = async () => {
    const message = input.trim();
    if (!message || sending) return;
    await loadHistory();
    setInput(""); setSending(true);
    setMessages((current) => [...current, { id: crypto.randomUUID(), role: "user", content: message }, { id: "streaming", role: "assistant", content: "" }]);
    try {
      await streamSSE("/assistant/chat", { session_id: sessionId, message, page: location.pathname, site_id: siteId || undefined }, (chunk) => {
        setMessages((current) => current.map((item) => item.id === "streaming" ? { ...item, content: item.content + chunk } : item));
      });
      setMessages((current) => current.map((item) => item.id === "streaming" ? { ...item, id: crypto.randomUUID() } : item));
    } catch (error) {
      setMessages((current) => current.map((item) => item.id === "streaming" ? { ...item, content: `I couldn't complete that diagnosis: ${error.message}` } : item));
    } finally { setSending(false); }
  };

  return <div className="doctor-assistant" data-testid="ai-doctor-assistant">
    {bubbleVisible && currentSuggestion && !open && <aside className="doctor-proactive-bubble" data-testid="doctor-proactive-suggestion">
      <button className="doctor-dismiss" onClick={() => dismiss(currentSuggestion)} data-testid="dismiss-doctor-suggestion-button" aria-label="Dismiss suggestion"><X size={14}/></button>
      <span>{currentSuggestion.module}</span><strong>{currentSuggestion.title}</strong><p>{currentSuggestion.message}</p>
      <Button size="sm" onClick={() => takeAction(currentSuggestion)} data-testid="doctor-suggestion-action-button">{currentSuggestion.action_label}<ArrowRight size={14}/></Button>
    </aside>}
    {open && <section className="doctor-panel" data-testid="ai-doctor-chat-panel">
      <header><div><span className="doctor-online"><i/> Proactive assistant</span><h2>SEO Rank Doctor</h2></div><Button variant="ghost" size="icon" onClick={() => setOpen(false)} data-testid="close-ai-doctor-button" aria-label="Close AI Doctor"><X size={19}/></Button></header>
      <div className="doctor-priorities" data-testid="doctor-priority-list">{data.suggestions.slice(0, 3).map((item) => <article key={item.id}><span>{item.module}</span><p>{item.message}</p><Button variant="ghost" size="sm" onClick={() => takeAction(item)} data-testid={`doctor-action-${item.id}-button`}>{item.action_label}<ArrowRight size={13}/></Button></article>)}</div>
      <div className="doctor-chat" data-testid="doctor-chat-messages">{messages.length === 0 && <div className="doctor-welcome"><Bot size={24}/><strong>Ask me what to prioritize.</strong><p>I can diagnose your content, rankings, backlinks, AI visibility, social queue, and live traffic.</p></div>}{messages.map((item) => <div className={`doctor-message ${item.role}`} key={item.id} data-testid={`doctor-message-${item.role}-${item.id}`}>{item.role === "assistant" ? <div className="doctor-message-rich"><ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content || (sending ? "Diagnosing…" : "")}</ReactMarkdown></div> : <p>{item.content}</p>}</div>)}<div ref={messagesEnd}/></div>
      <footer><Textarea value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendMessage(); } }} placeholder="Ask your SEO Doctor…" data-testid="doctor-chat-input"/><Button size="icon" onClick={sendMessage} disabled={!input.trim() || sending} data-testid="send-doctor-message-button" aria-label="Send message">{sending ? <LoaderCircle className="spin" size={18}/> : <Send size={18}/>}</Button></footer>
    </section>}
    <button className="doctor-launcher" onClick={() => { setOpen((value) => !value); setBubbleVisible(false); }} data-testid="open-ai-doctor-button" aria-label="Open proactive AI Doctor">
      <span className="doctor-spark"><Sparkles size={14}/></span><img src={assets.doctorAssistant} alt="SEO Rank Doctor assistant"/><span className="doctor-label">Ask AI Doctor</span>{currentSuggestion && <i className="doctor-alert"/>}
    </button>
  </div>;
}
